/**
 * Gallery
 * 採用ギャラリー。マークアップは「全件のフラットリスト（.p-gallery-track 1個）」だけ。
 * 表示モードは **実際の <li> 件数** と **data属性のしきい値（PC/SP別）** からランタイムで決める
 * （枚数はMT登録で動的＝ビルド時に確定しない。ビューポートでしきい値が変わるためリサイズで再判定）。
 *
 *   data-gallery-fixed-max     (PC・既定4)  … n ≤ X → fixed（固定280boxを中央寄せ・静止）
 *   data-gallery-flow-from     (PC・既定10) … n ≥ Z → 上下2行の自動横流れ
 *   data-gallery-fixed-max-sp  (SP・既定2)
 *   data-gallery-flow-from-sp  (SP・既定4)
 *   （X < n < Z）                          … 1行の自動横流れ（＝「スライダー」）
 *   data-loop-speed                        … 流れる速度（px/秒・既定40）。**PC基準の値**で、
 *                                            他の幅では1枚ぶんの送り幅の比で換算する（下記）
 *
 * 速度は「1枚が通り過ぎる秒数」がどの幅でも同じになるよう、1枚ぶんの送り幅（画像幅＋余白）を
 * PC基準（CSSの --pitch-pc）と比べて換算する。px/秒を固定すると、SPは画像が小さいぶん
 * 1枚あたりが倍速で流れて見える（PC 9.0秒/枚 に対し SP 4.4秒/枚 だった）。
 *
 * 2行に分けるときは前半/後半で分割し、上下の行に同じ画像を出さない。
 * 横流れは LogoLoop と同方式（CSSアニメ＋複製。1単位がコンテナ幅以上になるまで原本を繰り返し→2倍化し
 * translateX(-50%) で継ぎ目なくループ）。操作ボタン（アロー）は無し。2行時は2行目を逆方向に流す。
 * reduced-motion時は固定（静止）。
 *
 * @example
 * <div class="p-gallery js-gallery"
 *      data-gallery-fixed-max="4" data-gallery-flow-from="10"
 *      data-gallery-fixed-max-sp="2" data-gallery-flow-from-sp="4" data-loop-speed="40">
 *   <ul class="p-gallery-track"><li>…</li> …全件… </ul>
 * </div>
 */
export default class Gallery {
  constructor(options = {}) {
    this.selector = options.selector || '.js-gallery';
    this.trackSelector = '.p-gallery-track';
    this.breakpoint = options.breakpoint || 768;
    this.reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.roots = Array.from(document.querySelectorAll(this.selector));
    this.roots.forEach((root) => this.setup(root));

    // ビューポートでしきい値（＝モード）が変わるのでリサイズで再判定（reduced-motionは常に固定＝CSSで足りる）
    if (!this.reduce && this.roots.length) {
      let timer = null;
      window.addEventListener('resize', () => {
        clearTimeout(timer);
        timer = window.setTimeout(() => this.roots.forEach((root) => this.build(root)), 200);
      });
    }
  }

  /**
   * 全件を退避して初回ビルド（モード判定は build 側＝ビューポート×件数で都度決める）
   * @param {HTMLElement} root
   */
  setup(root) {
    const track = root.querySelector(this.trackSelector);
    if (!track || !track.children.length) return;
    root._galleryItemsHTML = track.innerHTML;
    this.build(root);
  }

  /**
   * 件数×しきい値（PC/SP別）でモード（fixed / 1行流れ / 2行流れ）を決めて組む（初期化・リサイズ共通）
   * @param {HTMLElement} root
   */
  build(root) {
    if (root._galleryItemsHTML == null) return;

    const isSp = window.innerWidth < this.breakpoint;
    const x = isSp
      ? (parseInt(root.dataset.galleryFixedMaxSp, 10) || 2)
      : (parseInt(root.dataset.galleryFixedMax, 10) || 4);
    const z = isSp
      ? (parseInt(root.dataset.galleryFlowFromSp, 10) || 4)
      : (parseInt(root.dataset.galleryFlowFrom, 10) || 10);

    const holder = document.createElement('div');
    holder.innerHTML = root._galleryItemsHTML;
    const items = Array.from(holder.children);
    if (!items.length) return;
    const n = items.length;

    root.querySelectorAll(this.trackSelector).forEach((t) => t.remove());
    root.classList.remove('is-fixed', 'is-flow');

    // 固定（n ≤ X）／reduced-motion＝静止（CSSがグリッド／中央寄せにする）
    if (n <= x || this.reduce) {
      root.classList.add('is-fixed');
      root.dataset.galleryMode = 'fixed';
      root.appendChild(this.makeTrack(items));
      return;
    }

    // 横流れ：n ≥ Z は2行、それ未満は1行
    const rows = n >= z ? 2 : 1;
    root.classList.add('is-flow');
    root.dataset.galleryMode = rows === 2 ? 'flow-2' : 'flow-1';

    // 2行は前半/後半で分割＝上下で同じ画像を出さない（各行は自分の画像だけをループ）
    const half = Math.ceil(items.length / 2);
    const groups = rows === 2 ? [items.slice(0, half), items.slice(half)] : [items];

    const speedPc = parseFloat(root.dataset.loopSpeed) || 40;
    const pitchPc = parseFloat(getComputedStyle(root).getPropertyValue('--pitch-pc'));
    const containerWidth = root.clientWidth;

    groups.forEach((group) => {
      if (!group.length) return;
      const track = this.makeTrack(group);
      root.appendChild(track);

      // 1単位がコンテナ幅以上になるまで原本を繰り返す→2倍化（右端に空白が出ないように）
      const base = Array.from(track.children);
      let guard = 0;
      while (track.scrollWidth < containerWidth && guard < 50) {
        base.forEach((node) => track.appendChild(this.cloneItem(node)));
        guard += 1;
      }
      const unitWidth = track.scrollWidth;

      // 1枚ぶんの送り幅をPC基準と比べて速度を換算する（--pitch-pc が読めなければPCの値のまま）
      const first = track.children[0];
      const pitch = first.getBoundingClientRect().width + parseFloat(getComputedStyle(first).marginRight);
      const speed = pitchPc > 0 && pitch > 0 ? speedPc * (pitch / pitchPc) : speedPc;

      Array.from(track.children).forEach((node) => track.appendChild(this.cloneItem(node)));

      if (unitWidth > 0 && speed > 0) {
        track.style.setProperty('--loop-duration', `${unitWidth / speed}s`);
      }
    });
  }

  makeTrack(items) {
    const track = document.createElement('ul');
    track.className = 'p-gallery-track';
    items.forEach((node) => track.appendChild(node.cloneNode(true)));
    return track;
  }

  cloneItem(node) {
    const clone = node.cloneNode(true);
    clone.setAttribute('aria-hidden', 'true');
    return clone;
  }
}
